import CheckOutlined from '@mui/icons-material/CheckOutlined';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import QuestionMarkOutlined from '@mui/icons-material/QuestionMarkOutlined';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import { lighten, useTheme } from '@mui/material';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import { SxProps, Theme } from '@mui/material/styles';
import { SvgIconProps } from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { AiReviewStatus } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { ComponentType, ReactNode, useCallback, useState } from 'react';

import { createdBy as principalName } from '~components/Job/AuditDetails/utils';
import { AiReview } from '~hooks/useAiReview/models';
import useHoverPopover from '~hooks/useHoverPopover';
import { Nullable } from '~types/Nullable';

interface AiReviewBadgeProps {
  status: AiReviewStatus;
  size?: 'sm' | 'md';
}

export function getStatusColor(theme: Theme, status: AiReviewStatus) {
  switch (status) {
    case AiReviewStatus.APPROVED:
      return theme.palette.success.main;
    case AiReviewStatus.UNREADABLE:
      return theme.semantic.statusWarning;
    case AiReviewStatus.FLAGGED:
      return theme.palette.error.main;
    default:
      throw new Error(`Unknown AI review status: ${status}`);
  }
}

function getStatusIcon(status: AiReviewStatus) {
  switch (status) {
    case AiReviewStatus.APPROVED:
      return CheckOutlined;
    case AiReviewStatus.UNREADABLE:
      return QuestionMarkOutlined;
    case AiReviewStatus.FLAGGED:
      return FlagOutlined;
    default:
      throw new Error(`Unknown AI review status: ${status}`);
  }
}

export const badgeSizes = {
  sm: { box: '18px', icon: '10px', robot: '10px' },
  md: { box: '24px', icon: '14px', robot: '13px' },
};

interface RobotBadgeProps {
  children: ReactNode;
  color: string;
  dataTestId: string;
  robotSize: string;
}

/**
 * Pins the robot marker to the corner of a review badge, so the slot always says who did
 * the reviewing.
 */
function RobotBadge({ children, color, dataTestId, robotSize }: RobotBadgeProps) {
  return (
    <Badge
      data-test-id={dataTestId}
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            backgroundColor: lighten(color, 0.95),
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            p: '1px',
          }}
        >
          <SmartToyOutlined sx={{ color, fontSize: robotSize }} />
        </Box>
      }
    >
      {children}
    </Badge>
  );
}

export function AiReviewBadge({ status, size = 'md' }: AiReviewBadgeProps) {
  const StatusIcon = getStatusIcon(status);
  const s = badgeSizes[size];
  const theme = useTheme();
  const statusColor = getStatusColor(theme, status);

  return (
    <RobotBadge
      color={statusColor}
      dataTestId={`ai-review-badge-${status}`}
      robotSize={s.robot}
    >
      <Box
        component="span"
        sx={{
          alignItems: 'center',
          backgroundColor: lighten(statusColor, 0.95),
          border: `solid 1px ${statusColor}`,
          borderRadius: '50%',
          cursor: 'default',
          display: 'flex',
          height: s.box,
          width: s.box,
          justifyContent: 'center',
        }}
      >
        <StatusIcon
          data-test-id={`ai-review-badge-${status}-icon`}
          sx={{ color: statusColor, height: s.icon, width: s.icon }}
        />
      </Box>
    </RobotBadge>
  );
}

interface OutlinedBadgeProps {
  ariaLabel: string;
  dataTestId: string;
  Icon?: ComponentType<SvgIconProps>;
  sx?: SxProps<Theme>;
}

/**
 * The badge for the states where there is nothing live to say — a load the AI never
 * reviewed, or a review or flag that was dismissed. The dashed outline distinguishes them
 * from a live verdict while keeping the slot filled, so a column of rows stays aligned.
 */
export function OutlinedBadge({
  ariaLabel,
  dataTestId,
  Icon = SmartToyOutlined,
  sx,
}: OutlinedBadgeProps) {
  return (
    <Box
      aria-label={ariaLabel}
      component="span"
      data-test-id={dataTestId}
      sx={[
        (theme: Theme) => ({
          alignItems: 'center',
          backgroundColor: theme.semantic.surfaceRaised,
          border: `dashed 1px ${theme.brandV2.colors.treadGray7}`,
          borderRadius: '50%',
          cursor: 'default',
          display: 'flex',
          height: badgeSizes.md.box,
          justifyContent: 'center',
          width: badgeSizes.md.box,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Icon
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray7,
          height: badgeSizes.md.icon,
          width: badgeSizes.md.icon,
        })}
      />
    </Box>
  );
}

export function DismissedAiReviewBadge({ sx }: { sx?: SxProps<Theme> }) {
  return (
    <OutlinedBadge
      ariaLabel={t('approvals.ai_review.dismissed')}
      dataTestId="ai-review-badge-dismissed"
      sx={sx}
    />
  );
}

function NoAiReviewBadge() {
  return (
    <OutlinedBadge
      ariaLabel={t('approvals.ai_review.no_review_title')}
      dataTestId="ai-review-badge-none"
    />
  );
}

interface ActionButtonProps {
  children: string;
  dataTestId: string;
  isBusy?: boolean;
  onClick: () => void;
}

/**
 * The dismiss and undo controls sit at the height of the Approve button beside them, but
 * stay visually neutral rather than taking a palette colour — they are corrections, not
 * the row's primary action. `SmallButton` cannot be used: it pins height and padding under
 * a `&.MuiButtonBase-root` selector that outranks an `sx` override.
 *
 * An in-flight request greys the button out rather than swapping in a spinner. These calls
 * return fast enough that a spinner only ever flashed, and it widened the button as it came
 * and went.
 */
export function OutlinedActionButton({
  children,
  dataTestId,
  isBusy = false,
  onClick,
}: ActionButtonProps) {
  return (
    <Button
      color="inherit"
      data-test-id={dataTestId}
      disabled={isBusy}
      onClick={onClick}
      variant="outlined"
      sx={(theme) => ({
        backgroundColor: theme.semantic.surfaceRaised,
        border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        fontSize: '12px',
        height: '24px',
        minWidth: 0,
        px: 1,
        py: 0,
        whiteSpace: 'nowrap',
      })}
    >
      {children}
    </Button>
  );
}

interface DismissFooterProps {
  buttonLabel?: string;
  dataTestId?: string;
  isDismissing?: boolean;
  onDismiss: () => void;
  prompt?: string;
}

/**
 * Closes out a live review or flag with the way to overrule it. Shared by the row's hover
 * cards and the ticket drawer so they read the same.
 */
export function DismissFooter({
  buttonLabel = t('approvals.ai_review.dismiss'),
  dataTestId = 'ai-review-dismiss',
  isDismissing = false,
  onDismiss,
  prompt = t('approvals.ai_review.dismiss_prompt'),
}: DismissFooterProps) {
  return (
    <>
      <Divider sx={{ my: 1.5 }} />

      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="body2"
          sx={(theme) => ({ color: theme.brandV2.colors.treadGray3 })}
        >
          {prompt}
        </Typography>

        <OutlinedActionButton
          dataTestId={dataTestId}
          isBusy={isDismissing}
          onClick={onDismiss}
        >
          {buttonLabel}
        </OutlinedActionButton>
      </Box>
    </>
  );
}

interface DismissedBannerProps {
  badge: ReactNode;
  children: ReactNode;
  dataTestId: string;
  sx?: SxProps<Theme>;
}

/**
 * The neutral strip the ticket drawer shows for a dismissed review or flag: the dashed
 * badge on the left and the dismissal read-out beside it.
 */
export function DismissedBanner({
  badge,
  children,
  dataTestId,
  sx,
}: DismissedBannerProps) {
  return (
    <Box
      data-test-id={dataTestId}
      sx={[
        (theme: Theme) => ({
          alignItems: 'center',
          backgroundColor: theme.semantic.chipNeutralBg,
          borderRadius: theme.brandV2.borderRadius,
          display: 'flex',
          gap: 1,
          p: theme.spacing(1, 1.5),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {badge}

      {children}
    </Box>
  );
}

interface DismissalDetailsProps {
  dismissedAt: Nullable<Dayjs>;
  isUndoing?: boolean;
  label: string;
  onUndo?: () => void;
  undoDataTestId: string;
}

/**
 * The dismissal read-out shared by the row's hover card and the ticket drawer: one line
 * saying who overruled the review or flag and when, with undo held out to the right.
 */
export function DismissalDetails({
  dismissedAt,
  isUndoing = false,
  label,
  onUndo,
  undoDataTestId,
}: DismissalDetailsProps) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexGrow: 1,
        gap: 2,
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography
          variant="body2"
          sx={(theme) => ({ color: theme.brandV2.colors.treadBlack })}
        >
          {label}
        </Typography>

        {dismissedAt && (
          <Typography
            variant="body2"
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray3,
              fontSize: '11px',
            })}
          >
            {dayjs.tz(dismissedAt).fromNow()}
          </Typography>
        )}
      </Box>

      {onUndo && (
        <OutlinedActionButton
          dataTestId={undoDataTestId}
          isBusy={isUndoing}
          onClick={onUndo}
        >
          {t('approvals.ai_review.undo')}
        </OutlinedActionButton>
      )}
    </Box>
  );
}

interface DismissedAiReviewDetailsProps {
  aiReview: AiReview;
  isUndoing?: boolean;
  onUndo?: () => void;
}

export function DismissedAiReviewDetails({
  aiReview,
  isUndoing = false,
  onUndo,
}: DismissedAiReviewDetailsProps) {
  const dismissedByName = aiReview.dismissedBy
    ? principalName(aiReview.dismissedBy)
    : undefined;
  const dismissedLabel = dismissedByName
    ? t('approvals.ai_review.dismissed_by', { name: dismissedByName })
    : t('approvals.ai_review.dismissed');

  return (
    <DismissalDetails
      dismissedAt={aiReview.dismissedAt}
      isUndoing={isUndoing}
      label={dismissedLabel}
      onUndo={onUndo}
      undoDataTestId="ai-review-undo-dismiss"
    />
  );
}

/**
 * Runs a popover action, keeping the card open when it fails so the control can be tried
 * again. The rejection is swallowed because the caller already reported it and React
 * invokes these handlers without awaiting them.
 */
export function usePopoverAction(
  action: (() => Promise<void>) | undefined,
  close: () => void,
) {
  const [isRunning, setIsRunning] = useState(false);

  const run = useCallback(async () => {
    if (!action) {
      return;
    }

    setIsRunning(true);

    try {
      await action();
      close();
    } catch {
      // Already reported by the action itself.
    } finally {
      setIsRunning(false);
    }
  }, [action, close]);

  return { isRunning, run };
}

interface DismissibleHoverCardProps {
  badge: ReactNode;
  isPinnable: boolean;
  renderContent: (close: () => void) => ReactNode;
}

export function DismissibleHoverCard({
  badge,
  isPinnable,
  renderContent,
}: DismissibleHoverCardProps) {
  const { anchorEl, isOpen, isPinned, triggerProps, popoverHandlers, close } =
    useHoverPopover({ isPinnable });

  return (
    <>
      <Box
        {...triggerProps}
        onClick={(event) => {
          event.stopPropagation();
          triggerProps.onClick?.(event);
        }}
        sx={{ cursor: 'default' }}
      >
        {badge}
      </Box>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              mt: -1,
              pointerEvents: 'auto',
              userSelect: 'text',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              '& *': { userSelect: 'text' },
            },
            onClick: (e) => e.stopPropagation(),
            onMouseEnter: popoverHandlers.onMouseEnter,
            onMouseLeave: popoverHandlers.onMouseLeave,
          },
          root: {
            // While merely hovering, the full-screen root has to let clicks through so
            // the page stays usable. Once pinned it has to catch them instead, or MUI
            // never sees the click-away that should close the card.
            style: { pointerEvents: isPinned ? 'auto' : 'none' },
          },
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
      >
        {renderContent(close)}
      </Popover>
    </>
  );
}

/**
 * Fills the badge slot of a load the AI never reviewed. The hover card exists to answer the
 * question the empty-looking badge raises — the AI skipped this load, it did not pass it.
 */
export function NoAiReviewIndicator() {
  return (
    <DismissibleHoverCard
      badge={<NoAiReviewBadge />}
      // The card holds nothing to click, but pinning is still the only way a touch device
      // can open it at all — those never fire the hover that would otherwise do it.
      isPinnable
      renderContent={() => <NoAiReviewPopoverContent />}
    />
  );
}

function NoAiReviewPopoverContent() {
  return (
    <Box sx={{ p: 2, maxWidth: 350 }}>
      <Typography
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: '12px',
          fontWeight: 600,
        })}
      >
        {t('approvals.ai_review.no_review_title')}
      </Typography>
      <Typography
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: '12px',
          fontWeight: 400,
        })}
      >
        {t('approvals.ai_review.no_review_description')}
      </Typography>
    </Box>
  );
}

interface DismissedAiReviewIndicatorProps {
  aiReview: AiReview;
  canUndo?: boolean;
  onUndoDismiss?: () => Promise<void>;
}

export function DismissedAiReviewIndicator({
  aiReview,
  canUndo = false,
  onUndoDismiss,
}: DismissedAiReviewIndicatorProps) {
  const isUndoable = canUndo && Boolean(onUndoDismiss);

  return (
    <DismissibleHoverCard
      badge={<DismissedAiReviewBadge />}
      isPinnable={isUndoable}
      renderContent={(close) => (
        <DismissedAiReviewPopoverContent
          aiReview={aiReview}
          close={close}
          onUndoDismiss={isUndoable ? onUndoDismiss : undefined}
        />
      )}
    />
  );
}

interface DismissedAiReviewPopoverContentProps {
  aiReview: AiReview;
  close: () => void;
  onUndoDismiss?: () => Promise<void>;
}

function DismissedAiReviewPopoverContent({
  aiReview,
  close,
  onUndoDismiss,
}: DismissedAiReviewPopoverContentProps) {
  const { isRunning, run } = usePopoverAction(onUndoDismiss, close);

  return (
    <Box sx={{ display: 'flex', p: 2, maxWidth: 350, minWidth: 220 }}>
      <DismissedAiReviewDetails
        aiReview={aiReview}
        isUndoing={isRunning}
        onUndo={onUndoDismiss ? run : undefined}
      />
    </Box>
  );
}

interface AiReviewIndicatorProps {
  aiReview: AiReview;
  canDismiss?: boolean;
  onDismiss?: () => Promise<void>;
}

export default function AiReviewIndicator({
  aiReview,
  canDismiss = false,
  onDismiss,
}: AiReviewIndicatorProps) {
  const isDismissible = canDismiss && Boolean(onDismiss);

  return (
    <DismissibleHoverCard
      badge={<AiReviewBadge status={aiReview.status} />}
      // Hovering is the only way to reach a popover that is not pinnable, which would
      // leave the dismiss button unusable by keyboard and on touch.
      isPinnable={isDismissible}
      renderContent={(close) => (
        <AiReviewPopoverContent
          aiReview={aiReview}
          close={close}
          onDismiss={isDismissible ? onDismiss : undefined}
        />
      )}
    />
  );
}

interface AiReviewPopoverContentProps {
  aiReview: AiReview;
  close: () => void;
  onDismiss?: () => Promise<void>;
}

function AiReviewPopoverContent({
  aiReview,
  close,
  onDismiss,
}: AiReviewPopoverContentProps) {
  const { isRunning: isDismissing, run: handleDismiss } = usePopoverAction(
    onDismiss,
    close,
  );
  const issuesCount = aiReview.issues.length;
  const hasIssues = issuesCount > 0;
  const timeAgo = aiReview.createdAt.fromNow();
  const StatusIcon = getStatusIcon(aiReview.status);

  return (
    <Box sx={{ p: 2, maxWidth: 350, minWidth: 250 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <Box>
          <Typography
            sx={(theme) => ({ color: theme.brandV2.colors.treadBlack, fontWeight: 600 })}
          >
            {t(`approvals.ai_review.status.${aiReview.status}`)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StatusIcon
              sx={(theme) => ({
                color: getStatusColor(theme, aiReview.status),
                fontSize: '14px',
              })}
            />
            <Typography
              variant="body2"
              sx={(theme) => ({ color: getStatusColor(theme, aiReview.status) })}
            >
              {t('approvals.ai_review.issues_found', { count: issuesCount })}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <SmartToyOutlined
            sx={(theme) => ({
              color: theme.brandV2.colors.treadBlue,
              fontSize: '14px',
              mt: '2px',
            })}
          />
          <Box sx={{ textAlign: 'right', pt: 0.25 }}>
            <Typography
              variant="body2"
              sx={(theme) => ({
                color: theme.brandV2.colors.treadBlue,
                fontWeight: 500,
              })}
            >
              {t('approvals.ai_review.reviewed_by_ai')}
            </Typography>
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray4,
                fontSize: '11px',
              })}
            >
              {timeAgo}
            </Typography>
          </Box>
        </Box>
      </Box>

      {hasIssues && (
        <Box component="ul" sx={{ m: 0, pl: 2, mb: 1.5 }}>
          {aiReview.issues.map((issue, index) => (
            <Typography
              key={index}
              variant="body2"
              component="li"
              sx={(theme) => ({
                color: theme.brandV2.colors.treadBlack,
                lineHeight: 1.4,
                listStyleType: 'square',
              })}
            >
              {issue}
            </Typography>
          ))}
        </Box>
      )}

      {aiReview.reasoning && (
        <Typography
          variant="body2"
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGray3,
            lineHeight: 1.4,
          })}
        >
          {aiReview.reasoning}
        </Typography>
      )}

      {onDismiss && (
        <DismissFooter isDismissing={isDismissing} onDismiss={handleDismiss} />
      )}
    </Box>
  );
}
